/**
 * @file `/` — resume-first home.
 * @module @labcraft/lab/ui/pages/home
 *
 * Umbrella #927 / part #940. The returning learner's first screen (no
 * longer a redirect to `/courses`). Two blocks:
 *
 *   - **Resume hero** — completion-aware. The `currentChapter` pointer
 *     is only ever the *last chapter visited*; it never advances on
 *     completion. So the hero cross-checks that pointer against the
 *     course's passed-Part set ({@link usePassedPartIds}) + the Parts
 *     path ({@link computePartsPath}) and renders one of:
 *       - **resume** — the pointed-at Part isn't done yet; pick up where
 *         you left off (with the snapshot promise, #926).
 *       - **next** — that Part is done but the course has a further
 *         unfinished Part; start its first chapter.
 *       - **done** — every Part is passed; the course is complete, so
 *         offer the catalog instead of re-resuming a finished chapter.
 *       - **start** — no chapter pointer at all (first visit).
 *   - **Catalog link** — into `/catalog` (the full course list).
 *
 * The machine-state line is render-only (the snapshot promise), not a
 * live terminal WS — the WS opens on the reader, not here.
 */

import { useEffect, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import { track } from '@labcraft/ui/hooks'
import type { Lab } from '../../client/index.ts'
import type { CourseSummary } from '../../client/courses.ts'
import { useMe, usePassedPartIds } from '../hooks/index.ts'
import { computePartsPath } from '../lib/parts_path.ts'
import styles from './home.module.css'

export function HomePage(): JSX.Element | null {
  const app = useClient<Lab>()
  const navigate = useNavigate()
  const me = useMe()
  const learner = me.data?.learner ?? null
  const currentChapter = learner?.currentChapter ?? null
  const courseId = currentChapter?.courseId ?? null
  // Passed-Part set for the pointer's course — the same completion source
  // the course landing trusts for its green "Done". Disabled (no fetch)
  // when there's no pointer; see `usePassedPartIds`.
  const passed = usePassedPartIds(courseId ?? '')
  const [summary, setSummary] = useState<CourseSummary | null>(null)

  useEffect(() => {
    if (!courseId) {
      setSummary(null)
      return
    }
    let active = true
    app.courses
      .get(courseId)
      .then(found => {
        if (active) setSummary(found)
      })
      .catch(() => {
        if (active) setSummary(null)
      })
    return () => {
      active = false
    }
  }, [app, courseId])

  if (!learner) return null

  const firstName = learner.displayName.split(' ')[0] || learner.displayName

  // Place the pointer on the course's Parts path. `now` = the current Part
  // still in progress → resume. `done` = the current Part is passed → the
  // pointer is stale as a resume target, so look for the next unfinished
  // Part. Until the summary + pass-state load, `path` is null and the hero
  // falls back to `resume` (matching the pre-load title fallback below).
  const path =
    currentChapter && summary
      ? computePartsPath(
          summary.parts,
          currentChapter.partId,
          passed.data ?? [],
        )
      : null
  const currentDone =
    path?.find(e => e.part.id === currentChapter?.partId)?.state === 'done'
  const nextEntry = currentDone
    ? (path?.find(e => e.state === 'next') ?? null)
    : null
  const nextChapter = nextEntry?.part.chapters[0] ?? null

  // Which hero to render. `start` when there's no pointer; otherwise the
  // pointer is done→(next|done) or still-in-progress→resume.
  const heroKind: 'start' | 'resume' | 'next' | 'done' = !currentChapter
    ? 'start'
    : currentDone
      ? nextEntry && nextChapter
        ? 'next'
        : 'done'
      : 'resume'

  // Human titles for the resume hero; fall back to the URL slugs when the
  // summary hasn't loaded (or the pointer is stale).
  const part = summary?.parts.find(p => p.id === currentChapter?.partId)
  const chapter = part?.chapters.find(c => c.id === currentChapter?.chapterId)
  const courseTitle = summary?.course.title ?? currentChapter?.courseId ?? ''
  const partTitle = part?.title ?? currentChapter?.partId ?? ''
  const chapterTitle = chapter?.title ?? currentChapter?.chapterId ?? ''

  function resume(): void {
    if (!currentChapter) return
    track('home_resume', {
      courseId: currentChapter.courseId,
      partId: currentChapter.partId,
      chapterId: currentChapter.chapterId,
    })
    navigate(
      `/courses/${currentChapter.courseId}/${currentChapter.partId}/${currentChapter.chapterId}`,
    )
  }

  function startNext(): void {
    if (!currentChapter || !nextEntry || !nextChapter) return
    track('home_start_next', {
      courseId: currentChapter.courseId,
      partId: nextEntry.part.id,
      chapterId: nextChapter.id,
    })
    navigate(
      `/courses/${currentChapter.courseId}/${nextEntry.part.id}/${nextChapter.id}`,
    )
  }

  function browseCatalog(): void {
    track('home_browse_catalog')
    navigate('/catalog')
  }

  return (
    <main className={styles.main}>
      <h1 className={styles.greeting}>Welcome back, {firstName}.</h1>

      {heroKind === 'resume' && (
        <section className={styles.hero} aria-label="Resume where you left off">
          <p className={styles.heroEyebrow}>Pick up where you left off</p>
          <p className={styles.heroTitle}>{chapterTitle}</p>
          <p className={styles.heroWhere}>
            {courseTitle} · {partTitle}
          </p>
          <p className={styles.snapshot}>
            <span className={styles.snapshotDot} aria-hidden />
            Paused, snapshotted · resumes in ~2s
          </p>
          <div>
            <button type="button" className={styles.resumeBtn} onClick={resume}>
              Resume <span aria-hidden>→</span>
            </button>
          </div>
        </section>
      )}

      {heroKind === 'next' && nextEntry && nextChapter && (
        <section className={styles.hero} aria-label="Start the next chapter">
          <p className={styles.heroEyebrow}>Up next</p>
          <p className={styles.heroTitle}>{nextChapter.title}</p>
          <p className={styles.heroWhere}>
            {courseTitle} · {nextEntry.part.title}
          </p>
          <div>
            <button
              type="button"
              className={styles.resumeBtn}
              onClick={startNext}>
              Start next <span aria-hidden>→</span>
            </button>
          </div>
        </section>
      )}

      {heroKind === 'done' && (
        <section className={styles.startCard} aria-label="Course complete">
          <p className={styles.startTitle}>You finished {courseTitle}.</p>
          <p className={styles.startLede}>
            That’s the whole course cleared. Pick your next one — the judgment
            compounds across them.
          </p>
          <button
            type="button"
            className={styles.resumeBtn}
            onClick={browseCatalog}>
            Browse courses <span aria-hidden>→</span>
          </button>
        </section>
      )}

      {heroKind === 'start' && (
        <section className={styles.startCard} aria-label="Start learning">
          <p className={styles.startTitle}>Start your first lab.</p>
          <p className={styles.startLede}>
            Real shells, deliberate failure beats, and the judgment you only
            build by trying things and watching them break.
          </p>
          <button
            type="button"
            className={styles.resumeBtn}
            onClick={browseCatalog}>
            Browse courses <span aria-hidden>→</span>
          </button>
        </section>
      )}

      {(heroKind === 'resume' || heroKind === 'next') && (
        <button
          type="button"
          className={styles.catalogLink}
          onClick={browseCatalog}>
          Browse all courses <span aria-hidden>→</span>
        </button>
      )}
    </main>
  )
}
